import { useEffect, useRef } from 'react'
import { IconArrowDown, IconArrowRight } from '../ui/Icon'
import RegMark from '../ui/RegMark'
import useReducedMotion from '../../hooks/useReducedMotion'
import { HERO_I18N } from '../../data/hero-i18n'
import './hero.css'

/*
 * Overlay geometry in the photo's own pixel space (portfolio-new-4: 1024 x 718).
 * The printed ISOFIX mark was measured at x 440-615, y 264-442 (centre 527.5, 353).
 */
const MARK = { x0: 418, y0: 242, x1: 637, y1: 464, cx: 527.5, cy: 353 }
const ARM = 30 // crop-mark arm length
const FLY = 64 // how far out the crop marks start, along their diagonals
const SLIDE = 84 // how far the registration targets slide along their axes

const CROPS = [
  { key: 'tl', d: `M${MARK.x0} ${MARK.y0 + ARM}V${MARK.y0}H${MARK.x0 + ARM}`, dx: -FLY, dy: -FLY },
  { key: 'tr', d: `M${MARK.x1 - ARM} ${MARK.y0}H${MARK.x1}V${MARK.y0 + ARM}`, dx: FLY, dy: -FLY },
  { key: 'br', d: `M${MARK.x1} ${MARK.y1 - ARM}V${MARK.y1}H${MARK.x1 - ARM}`, dx: FLY, dy: FLY },
  { key: 'bl', d: `M${MARK.x0 + ARM} ${MARK.y1}H${MARK.x0}V${MARK.y1 - ARM}`, dx: -FLY, dy: FLY },
]

// RegMark geometry (ring r 6.5 in a 24 box, crosshair edge to edge), scaled x2.
const TARGET_R = 13
const TARGET_ARM = 24
const TARGETS = [
  { key: 'h', cx: 366, cy: MARK.cy, dx: -SLIDE, dy: 0 },
  { key: 'v', cx: MARK.cx, cy: 186, dx: 0, dy: -SLIDE },
]

// Leader: from just outside the top-right crop mark to a shelf on the sweep.
// Desktop crops the photo's empty sweep (object-fit: cover, ~x 100-924 stays
// visible), so its shelf ends earlier than on the uncropped stacked layout.
const LEADER = { x0: 643, y0: 236, x1: 741, y1: 148, wide: 1000, cropped: 905 }
const PHOTO_W = 1024
const PHOTO_H = 718
const leaderPath = (end) => `M${LEADER.x0} ${LEADER.y0}L${LEADER.x1} ${LEADER.y1}H${end}`

// Desktop shows the photo cover-cropped (~1.25x the 46vw column), hence 57vw.
const PICTURE_SIZES = '(max-width: 1079px) 92vw, 57vw'
const AVIF_SRCSET =
  '/portfolio-new-4-540w.avif 540w, /portfolio-new-4-720w.avif 720w, /portfolio-new-4-1024w.avif 1024w'
const WEBP_SRCSET =
  '/portfolio-new-4-540w.webp 540w, /portfolio-new-4-720w.webp 720w, /portfolio-new-4.webp 1024w'

const PARALLAX_PX = 6

/**
 * Starts the registration animation once the photo is on screen. Writes a
 * data attribute instead of React state so nothing re-renders:
 *   (absent) — prerender / no JS: decorations stay hidden
 *   play     — the marks converge, the leader draws, the label prints
 *   static   — reduced motion: final state, no movement
 */
function useRegistration(visualRef, imgRef, reducedMotion) {
  useEffect(() => {
    const visual = visualRef.current
    if (!visual || window.__ANKA_PRERENDER__) return undefined
    if (reducedMotion) {
      visual.dataset.reg = 'static'
      return undefined
    }
    const img = imgRef.current
    const start = () => {
      visual.dataset.reg = 'play'
    }
    if (!img || (img.complete && img.naturalWidth > 0)) {
      start()
      return undefined
    }
    img.addEventListener('load', start, { once: true })
    return () => img.removeEventListener('load', start)
  }, [visualRef, imgRef, reducedMotion])
}

/** Pointer parallax on the overlay (fine pointers only, <= 6px). */
function useOverlayParallax(visualRef, overlayRef, reducedMotion) {
  useEffect(() => {
    const visual = visualRef.current
    const overlay = overlayRef.current
    if (!visual || !overlay || reducedMotion || window.__ANKA_PRERENDER__) return undefined
    if (!window.matchMedia('(pointer: fine)').matches) return undefined

    let frame = 0
    let next = { x: 0, y: 0 }
    const write = () => {
      frame = 0
      overlay.style.setProperty('--mx', `${(next.x * PARALLAX_PX).toFixed(2)}px`)
      overlay.style.setProperty('--my', `${(next.y * PARALLAX_PX).toFixed(2)}px`)
    }
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(write)
    }
    const onMove = (event) => {
      const rect = visual.getBoundingClientRect()
      const x = ((event.clientX - rect.left) / rect.width - 0.5) * 2
      const y = ((event.clientY - rect.top) / rect.height - 0.5) * 2
      next = { x: Math.max(-1, Math.min(1, x)), y: Math.max(-1, Math.min(1, y)) }
      schedule()
    }
    const onLeave = () => {
      next = { x: 0, y: 0 }
      schedule()
    }
    const section = visual.closest('section') ?? visual
    section.addEventListener('pointermove', onMove, { passive: true })
    section.addEventListener('pointerleave', onLeave)
    return () => {
      section.removeEventListener('pointermove', onMove)
      section.removeEventListener('pointerleave', onLeave)
      if (frame) cancelAnimationFrame(frame)
    }
  }, [visualRef, overlayRef, reducedMotion])
}

function RegistrationOverlay() {
  return (
    <svg
      className="hero__marks"
      viewBox={`0 0 ${PHOTO_W} ${PHOTO_H}`}
      preserveAspectRatio="xMidYMid slice"
      fill="none"
      aria-hidden="true"
      focusable="false"
    >
      {CROPS.map((crop, i) => (
        <path
          key={crop.key}
          className="hero__reg hero__crop"
          d={crop.d}
          style={{ '--dx': `${crop.dx}px`, '--dy': `${crop.dy}px`, '--i': i }}
        />
      ))}
      {TARGETS.map((target, i) => (
        <g
          key={target.key}
          className="hero__reg hero__target"
          style={{ '--dx': `${target.dx}px`, '--dy': `${target.dy}px`, '--i': i }}
        >
          <circle cx={target.cx} cy={target.cy} r={TARGET_R} />
          <path
            d={`M${target.cx - TARGET_ARM} ${target.cy}H${target.cx + TARGET_ARM}M${target.cx} ${target.cy - TARGET_ARM}V${target.cy + TARGET_ARM}`}
          />
        </g>
      ))}
      <g className="hero__leader">
        <circle className="hero__leader-dot" cx={LEADER.x0} cy={LEADER.y0} r="3.5" />
        <path className="hero__leader-line hero__leader-line--wide" d={leaderPath(LEADER.wide)} />
        <path className="hero__leader-line hero__leader-line--cropped" d={leaderPath(LEADER.cropped)} />
      </g>
    </svg>
  )
}

/**
 * Home hero: the H1 and RFQ keys on the left, the printed ISOFIX cover on the
 * right with registration marks that converge on its mark (the "registration"
 * moment). Copy: src/data/hero-i18n.js.
 *
 * @param {{ lang?: 'tr' | 'en' | 'de' }} props
 */
export default function Hero({ lang = 'tr' }) {
  const t = HERO_I18N[lang] ?? HERO_I18N.tr
  const reducedMotion = useReducedMotion()
  const visualRef = useRef(null)
  const imgRef = useRef(null)
  const overlayRef = useRef(null)

  useRegistration(visualRef, imgRef, reducedMotion)
  useOverlayParallax(visualRef, overlayRef, reducedMotion)

  const labelStyle = {
    '--label-wide': LEADER.wide,
    '--label-cropped': LEADER.cropped,
    '--label-y': LEADER.y1,
  }

  return (
    <section className="hero" aria-labelledby="hero-title">
      <div className="container hero__grid">
        <div className="hero__text">
          <h1 id="hero-title" className="hero__title">
            {t.titleLead}{' '}
            <span className="hero__title-tail">{t.titleTail}</span>
          </h1>
          <p className="hero__lead">{t.description}</p>
          <div className="hero__ctas">
            <a href="#contact" className="btn">
              {t.ctaPrimary}
              <IconArrowRight />
            </a>
            <a href="#services" className="btn btn--ghost">
              {t.ctaSecondary}
              <IconArrowDown />
            </a>
          </div>
          <p className="hero__badge legend">
            <RegMark />
            {t.badge}
          </p>
        </div>

        <div className="hero__visual plate--marks" ref={visualRef}>
          <picture className="hero__photo">
            <source type="image/avif" srcSet={AVIF_SRCSET} sizes={PICTURE_SIZES} />
            <source type="image/webp" srcSet={WEBP_SRCSET} sizes={PICTURE_SIZES} />
            <img
              ref={imgRef}
              src="/portfolio-new-4.webp"
              alt={t.imageAlt}
              width={PHOTO_W}
              height={PHOTO_H}
              loading="eager"
              fetchPriority="high"
            />
          </picture>
          <div className="hero__overlay" ref={overlayRef} aria-hidden="true">
            <RegistrationOverlay />
            <p className="hero__label" style={labelStyle}>
              <span className="hero__label-service legend">{t.service}</span>
              <span className="hero__label-mark legend">{t.markName}</span>
            </p>
          </div>
        </div>
      </div>
    </section>
  )
}
